import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Discounts } from '@prisma/client';
import isEqual from 'lodash.isequal';
import { getInitiatePaymentPayload } from '../../helpers/payments';
import {
  CheckoutFormValues,
  SelectedAppointment,
} from '../../types/appointments';
import useUser from '../global/useUser';
import { getCheckoutData } from '../../services/payments';
import useDebouncedValue from '../useDebouncedValue';
import useCheckoutFormState from './useCheckoutFormState';

// Normalize form values for use as a React Query key and payload.
// Fields keyed separately (discount?.id) are stripped to prevent redundant
// fetches when they change. paymentMethod is canonicalized so FREE/undefined
// don't produce different cache entries for identical payloads.
/** The estimate prices an unpicked or FREE method as cash. */
export const getQuotedPaymentMethod = (
  paymentMethod: CheckoutFormValues['paymentMethod'],
) => (paymentMethod === 'FREE' || !paymentMethod ? 'CASH' : paymentMethod);

export function normalizeCheckoutQueryValues(
  values: CheckoutFormValues,
  depositMinimum: number,
): Omit<CheckoutFormValues, 'discountId'> & { paymentMethod: string } {
  const { discountId: omitted, ...rest } = values;
  return {
    ...rest,
    price:
      depositMinimum > 0
        ? Math.max(values.price, depositMinimum)
        : values.price,
    paymentMethod: getQuotedPaymentMethod(values.paymentMethod),
  };
}

// The subset that drives the request. Only customerId reaches the payload, so
// the other client fields must not trigger a refetch or a loading state.
function getCheckoutQueryValues(
  values: CheckoutFormValues,
  depositMinimum: number,
) {
  const normalized = normalizeCheckoutQueryValues(values, depositMinimum);
  return {
    ...normalized,
    customer: { customerId: normalized.customer?.customerId },
  };
}

function useGetCheckoutData({
  appointment,
  values,
  discount,
}: {
  appointment: Pick<
    SelectedAppointment,
    'appointmentId' | 'businessId' | 'depositPaidOn' | 'depositPrice'
  > | null;
  values: CheckoutFormValues;
  discount: Discounts | null;
}) {
  const { user } = useUser();
  const businessId = (appointment?.businessId || user?.business?.id)!;
  const { dispatch } = useCheckoutFormState();

  const debouncedValues = useDebouncedValue(values, 500);

  // Clamp price to deposit minimum for a stable query key → cache hit on blur.
  const depositMinimum =
    appointment?.depositPaidOn && (appointment?.depositPrice ?? 0) > 0
      ? appointment.depositPrice ?? 0
      : 0;

  const effectiveValues = React.useMemo(
    () => normalizeCheckoutQueryValues(debouncedValues, depositMinimum),
    [debouncedValues, depositMinimum],
  );

  // Debounced drives the request; live is only compared against it to know
  // whether a settled request is still pending.
  const queryValues = React.useMemo(
    () => getCheckoutQueryValues(debouncedValues, depositMinimum),
    [debouncedValues, depositMinimum],
  );
  const liveQueryValues = React.useMemo(
    () => getCheckoutQueryValues(values, depositMinimum),
    [values, depositMinimum],
  );

  const {
    data,
    isFetching,
    isLoading,
    isError,
    isSuccess,
    error,
    refetch,
    isPlaceholderData,
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'checkout-data',
      businessId,
      appointment?.appointmentId,
      appointment?.depositPaidOn,
      queryValues,
      discount?.id,
    ],
    queryFn: async ({ signal }) => {
      if (
        !effectiveValues.price &&
        !effectiveValues.additionalServices?.some(s => s.price > 0) &&
        !effectiveValues.customOrderItems?.some(
          item => item.price > 0 && item.quantity > 0,
        )
      )
        return null;

      const { data: payload } = getInitiatePaymentPayload({
        appointmentId: appointment?.appointmentId,
        businessId,
        values: effectiveValues,
        discount,
      });

      if (!payload.appointment && !payload.order) return null;

      const response = await getCheckoutData(payload, { signal });

      return response;
    },
    gcTime: 30000,
    staleTime: 30000,
    structuralSharing: false,
  });

  const isDebouncing = React.useMemo(
    () => !isEqual(liveQueryValues, queryValues),
    [liveQueryValues, queryValues],
  );
  React.useEffect(() => {
    dispatch({ type: 'SET_DEBOUNCING', payload: isDebouncing });

    return () => {
      dispatch({ type: 'SET_DEBOUNCING', payload: false });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDebouncing]);

  return {
    data,
    isPlaceholderData,
    isFetching,
    isLoading,
    isDebouncing,
    isError,
    isSuccess,
    error,
    refetch,
  };
}

export default useGetCheckoutData;
